import { Injectable } from '@angular/core';
import { Banner } from '@shared/entities/banner';
import { ApiService } from '@shared/services';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { BannerService } from './banner.service';

const LOG_PREFIX = '[Api][BannerForm] ';

@Injectable({
  providedIn: 'root'
})
export class BannerFormService extends ApiService<Banner> {
  constructor(private _bannerService: BannerService) {
    const masterUrl = '/banners';
    const detailUrl = '/banners';
    super(Banner, masterUrl, detailUrl, undefined, 'banner-form');
  }

  find(id: string): Observable<Banner | null> {
    this.logger.debug(LOG_PREFIX + `findBanner with id: ${id}`);
    return this.findOne(id).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'findBanner error', error);
        return of(null);
      })
    );
  }

  saveBanner(bannerData: Banner): Observable<Banner | null> {
    this.logger.debug(LOG_PREFIX + 'saveBanner', bannerData);
    return this.save(bannerData).pipe(
      map((response) => {
        this.logger.debug(LOG_PREFIX + 'saveBanner success', response);
        this._bannerService.refreshFilters();
        return response || bannerData;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'saveBanner error', error);
        return of(error);
      })
    );
  }

  editBanner(bannerData: Banner, bannerId: string): Observable<Banner | null> {
    this.logger.debug(LOG_PREFIX + `editBanner with id: ${bannerId}`, bannerData);
    return this.save(bannerData, bannerId).pipe(
      map((response) => {
        this.logger.debug(LOG_PREFIX + 'editBanner success', response);
        this._bannerService.refreshFilters();
        return response || bannerData;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'editBanner error', error);
        return of(error);
      })
    );
  }

  findByFilter(id: string): Observable<Banner | null> {
    this.logger.debug(LOG_PREFIX + `findByFilter banner with id: ${id}`);

    this.setUrlParameter('id', id);

    return this.paginate(Banner, 1, 1).pipe(
      map((paginate) => {
        if (paginate && paginate.data && paginate.data.length > 0) {
          return paginate.data[0];
        }
        return null;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'findByFilter error', error);
        return of(null);
      })
    );
  }

  deleteBanner(id: string): Observable<boolean | null> {
    this.logger.debug(LOG_PREFIX + `deleteBanner with id: ${id}`);
    this.setUrlParameter('id', id);
    return this.remove(id).pipe(
      map(() => {
        this.logger.debug(LOG_PREFIX + 'deleteBanner success');
        this._bannerService.refreshFilters();
        return true;
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'deleteBanner error', error);
        return of(error);
      })
    );
  }

  saveBannerWithAttachments(bannerData: Banner, files: Record<string, File>): Observable<Banner> {
    this.logger.debug(LOG_PREFIX + 'saveBannerWithAttachments', { bannerData, fileCount: Object.keys(files).length });

    const formData = new FormData();
    formData.append('request', JSON.stringify(bannerData));
    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });

    return this.postMultipartData<Banner>('/banners', formData, Banner).pipe(
      tap((banner) => {
        this.logger.debug(LOG_PREFIX + 'saveBannerWithAttachments success', banner);
        this._bannerService.refreshFilters();
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'saveBannerWithAttachments error', error);
        throw error;
      })
    );
  }

  updateBannerWithAttachments(bannerId: string, bannerData: Banner, files: Record<string, File>): Observable<Banner> {
    this.logger.debug(LOG_PREFIX + `updateBannerWithAttachments with id: ${bannerId}`, {
      bannerData,
      fileCount: Object.keys(files).length
    });

    const formData = new FormData();
    bannerData.id = bannerId;
    formData.append('request', JSON.stringify(bannerData));
    Object.entries(files).forEach(([key, file]) => {
      formData.append(key, file);
    });

    return this.putMultipartData<Banner>(`/banners?id=${bannerId}`, formData, Banner).pipe(
      tap((banner) => {
        this.logger.debug(LOG_PREFIX + 'updateBannerWithAttachments success', banner);
        this._bannerService.refreshFilters();
      }),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'updateBannerWithAttachments error', error);
        throw error;
      })
    );
  }
}
